import { useState } from 'react';

import { BackLink } from '@/components/BackLink';
import { Button } from '@/components/Button';
import { Callout } from '@/components/Callout';
import { DateField } from '@/components/DateField';
import { Field } from '@/components/Field';
import { KioskCard } from '@/components/KioskCard';
import { KioskScreen } from '@/components/KioskScreen';
import { Select } from '@/components/Select';
import { StatusBadge } from '@/components/StatusBadge';
import { BlockedScreen } from '@/features/application/components/BlockedScreen';
import { ConsentDialog } from '@/features/application/components/ConsentDialog';
import { BLOCKED_REASON, digitsOnly } from '@/features/application/types';
import type { TranslationKey } from '@/i18n/dictionary';
import { useTranslation } from '@/i18n/useTranslation';
import { captureCandidateSession, SCREEN, useKioskSession } from '@/lib/kioskSession';
import { sessionCandidateFromNames } from '@/lib/sessionCandidate';
import { saveProfile } from '@/services/candidate';
import { DIAGNOSIS } from '@/services/identityLookup';
import { todayIso } from '@/shared/calendar';
import { useFocusOnMount } from '@/shared/hooks/useFocusOnMount';
import { useScrollToFirstError } from '@/shared/hooks/useScrollToFirstError';

import { useReturningProfile } from '../hooks/useReturningProfile';
import type { MarkableField, ProfileUpdateErrors, ProfileUpdateValues } from '../types';
import {
  changesFrom,
  clearError,
  EMPTY_PROFILE_UPDATE,
  mergeOnFile,
  rejectionErrors,
  validateProfileUpdate,
} from '../types';

/**
 * Where a candidate the lookup DID find lands [Operator's rule, 2026-08-31].
 *
 * **The difference from `ApplicationScreen` is the premise, not the fields.** That one asks a stranger to
 * introduce themselves; this one shows somebody what ISS already holds and asks them to correct it — *"We've
 * filled in what's already on file — just review and edit anything that's changed."* Sending a returning
 * candidate to a blank form is what this screen exists to stop.
 *
 * It asks for more than the create form does — a shift preference, a home address and an emergency contact —
 * because those are things a record accumulates and a first application does not collect.
 *
 * ⭐ **The values come from the Bullhorn record** [0464 R2], read with `fetchCandidateProfile` once the
 * screen mounts — never from what was typed at check-in [ISS-221]. Continue waits for that read [0464 R1].
 *
 * ⭐ **Continue writes ONLY what the candidate changed** [0464 R3–R7], and nothing at all when nothing
 * changed — see `changesFrom`.
 *
 * ⛔ **The consent modal opens only when the record holds no consent** [0507, amending 0488]: *"solo se
 * pide el consentimiento si no esta en bullhorn"*. A candidate whose record already says so is never asked
 * again and the save sends no `consent`; one whose record does not — or whose read failed — is asked once,
 * and *I agree* writes `consent: true`, even with no field changed. A decline lands on `Blocked · consent`
 * with its way back to the modal [0160; 0314] and writes nothing.
 *
 * ⛔ **This screen asks NO eligibility question** — no *18 or older*, no *legally authorized to work* — and
 * so has no `Blocked · age` / `Blocked · auth` route [0479]. The returning profile frame draws neither. Those
 * two questions and the three-check gate are the CREATE's [0161; 0195; `ApplicationScreen`], unchanged.
 */
/**
 * Bullhorn's own shift vocabulary [`JobOrder.customText11` — «Shift Type», read 2026-09-02].
 *
 * The same five the openings carry, so a preference stated here is expressed in the words the jobs use.
 */
const SHIFT_OPTIONS = [
  'First Shift',
  'Second Shift',
  'Third Shift',
  'Weekend',
  'Various Shifts',
].map((shift) => ({ value: shift, label: shift }));

/**
 * What a field says when it is the one holding the save up.
 *
 * The specific message wherever this screen knows the rule — the two phones, the pay rate, the availability
 * date — and a general *"please check this field"* everywhere else. Each specific one also says that EMPTY
 * is allowed, because on this screen the value was pre-filled from Bullhorn rather than typed, and clearing
 * it is both the fastest way out and always accepted [0172].
 *
 * The everywhere-else case comes from the API refusing a value, and the only thing it hands back is an
 * untranslated English sentence written for a developer (`refusedFields` in `services/candidate.ts`), so a
 * specific message there would be invented rather than known.
 */
const ERROR_MESSAGE: Readonly<Record<MarkableField, TranslationKey>> = {
  firstName: 'profileUpdate.fieldRejected',
  lastName: 'profileUpdate.fieldRejected',
  email: 'application.email.error',
  phone: 'profileUpdate.phone.error',
  payRate: 'profileUpdate.payRate.error',
  dateAvailable: 'profileUpdate.dateAvailable.error',
  homeAddress: 'profileUpdate.fieldRejected',
  emergencyContact: 'profileUpdate.fieldRejected',
  emergencyPhone: 'profileUpdate.phone.error',
};

export function ProfileUpdateScreen() {
  const t = useTranslation();
  const heading = useFocusOnMount<HTMLHeadingElement>();
  const goTo = useKioskSession((state) => state.goTo);
  const factors = useKioskSession((state) => state.factors);
  const candidateId = useKioskSession((state) => state.lookupResult?.candidateId);
  const diagnosis = useKioskSession((state) => state.lookupResult?.diagnosis);
  const emailOnFile = useKioskSession((state) => state.lookupResult?.emailOnFile);
  const askToExit = useKioskSession((state) => state.askToExit);
  const enterSessionCandidate = useKioskSession((state) => state.enterSessionCandidate);
  const endCandidateSession = useKioskSession((state) => state.endCandidateSession);
  // The create form's slot, shared on purpose: the two screens never stand at once, and the session's end
  // clears it for both [`NO_CANDIDATE_SESSION`].
  const gate = useKioskSession((state) => state.applicationGate);
  const setGate = useKioskSession((state) => state.setApplicationGate);

  // Same rule as the profile page it mirrors: a returning candidate's own details, shown on a shared
  // screen, do not sit there after they leave [KID-1's clear-on-idle rule].
  const [saving, setSaving] = useState(false);

  /*
   * ⛔ **What was typed at check-in is a MATCH, not an answer** [ISS-221]. Check-in matches the surname
   * fuzzily and the email without regard to case, so `garcia` finds `Garcia-Lopez` and
   * `Maria.Garcia@Gmail.com` finds `maria.garcia@gmail.com`. Seeded into the fields, `mergeOnFile` — which
   * rightly never overwrites what the candidate typed — kept them, and the save wrote them over the record.
   * Left empty, both fill from the record; if the read fails they stay empty, and an empty field is not
   * sent, so the record keeps them. Only a change the candidate makes is written.
   *
   * ⭐ **The one exception is a CHOICE, and it is the email's:** a `differentEmail` match went through
   * `DifferentEmailDialog`, where the candidate chose which address to keep [0030, 0042]. There the session
   * email is their answer, not a match, so it is seeded — and a NEW address differs from the record's, so
   * `changesFrom` counts it as a change and the write carries it: an update, never a second record. Choosing
   * the address on file seeds the record's own value, which is no change and is not sent — measured against
   * the lookup's `emailOnFile` when the read failed (see `update`).
   */
  const choseTheirEmail = diagnosis === DIAGNOSIS.differentEmail;
  const [values, setValues] = useState<ProfileUpdateValues>({
    ...EMPTY_PROFILE_UPDATE,
    email: choseTheirEmail ? factors.email : '',
  });

  const [errors, setErrors] = useState<ProfileUpdateErrors>({});
  // Counts refused saves, so a second press with the same errors scrolls again. See the hook.
  const [refusals, setRefusals] = useState(0);
  useScrollToFirstError(refusals);
  /**
   * Whether the last attempt to continue got nowhere — a field this screen refused, or a field the API did.
   *
   * Separate from `errors` because it is only ever set by an ATTEMPT: a value pre-filled from Bullhorn can
   * be marked invalid the moment it arrives (below), and a summary appearing on a screen nobody has touched
   * yet would read as an accusation.
   */
  const [notSaved, setNotSaved] = useState(false);

  const set = (key: keyof ProfileUpdateValues, value: string): void => {
    setValues((current) => ({ ...current, [key]: value }));
    // Editing a field clears ITS error, never anybody else's — see `clearError`.
    setErrors((current) => clearError(current, key));
  };

  /*
   * **What Bullhorn already holds, so this screen CONFIRMS rather than asks.**
   *
   * Its heading says «We've filled in what's already on file — just review and edit anything that's
   * changed», and until 2026-09-02 that was true of the two check-in factors and nothing else: KID-2's
   * lookup returns a diagnosis and an id, never a record. A returning candidate was asked to retype what
   * ISS already had.
   *
   * Only fields the candidate has NOT touched are filled, so a read that lands late can never overwrite
   * something they just typed. Fails open: an unreadable profile leaves the form empty and they carry on
   * [BR-K7].
   *
   * ⛔ **Continue waits for the record** [0464 R1; ISS-228]. Pressed before the read answered, it saved a
   * screen still holding blanks — QA saw a blank first name, phone, pay rate and start date in 3 of 7 saves.
   * See the hook for why that can never hold the button for good.
   */
  const { loading, baseline, consentOnFile } = useReturningProfile((onFile) => {
    /*
     * ⚠️ **A pre-filled value the API will refuse is marked invalid AS IT ARRIVES, not on Continue** —
     * every one of them, not only the phone [decision 0172].
     *
     * The record holds values its own rules cannot express: `+541125484747` in the phone (read off a real
     * record on 2026-09-07), a `dateAvailable` from a visit six months ago, a pay rate somebody typed as
     * free text. `maxLength` bounds TYPING, never a programmatic fill — so the value the candidate never
     * chose sat in the box looking accepted, and refused the whole write with everything they DID type.
     *
     * Marking on arrival is the point: these are the fields on this screen they did not type and cannot
     * be expected to re-read. Nothing is corrected FOR them — trimming `541125484747` to ten digits gives
     * `5411254847`, a different, dialable, wrong number on a real person's record.
     */
    setErrors((current) => ({
      ...current,
      ...validateProfileUpdate(mergeOnFile(EMPTY_PROFILE_UPDATE, onFile)),
    }));
    setValues((current) => mergeOnFile(current, onFile));
  });

  /**
   * Confirms the corrections onto the existing record — an UPDATE, never a second one.
   *
   * KAP-7 forbids *"a second record for a candidate KID-2 already matched"*, the in-system-never-started
   * case included, so the id travels with the write and the backend turns it into `POST entity/Candidate/{id}`.
   *
   * The consent modal stands in front of it only when the record holds no consent [0507]; with consent on
   * file, Continue goes straight to {@link update}.
   */
  function confirm(): void {
    if (saving || loading) return;
    /*
     * ⚠️ **A correction this screen knows the API will refuse never leaves the screen** [decision 0172].
     *
     * The two phones, the pay rate and the availability date — every field here that has a shape at all.
     * One malformed optional value refused the ENTIRE write, so a rate typed as "twenty bucks" cost the
     * candidate the five fields beside it; see `validateProfileUpdate`.
     *
     * It still reads every field, the untouched ones included, although only a change now travels: 0464
     * leaves the screen's validation unchanged, and a malformed record value is cleared in one tap [0172].
     */
    const found = validateProfileUpdate(values);
    setErrors(found);
    setNotSaved(Object.keys(found).length > 0);
    if (Object.keys(found).length > 0) {
      setRefusals((n) => n + 1);
      return;
    }
    // No matched id is no record to hold a consent — and nothing is written below — so there is nothing to ask.
    if (!consentOnFile && candidateId !== undefined) {
      setGate({ stage: 'consent' });
      return;
    }
    // `update` owns its promise and never rejects: every failure becomes a value inside it.
    void update(false);
  }

  /**
   * The consent modal, asked because the record holds no consent [0507; 0160].
   *
   * A decline lands on `Blocked · consent` with **nothing written** — not even the fields they changed —
   * and its way back is the modal [0314]. *I agree* closes it and writes, carrying the consent.
   */
  async function answerConsent(consented: boolean): Promise<void> {
    if (saving) return;
    if (!consented) {
      setGate({ stage: 'blocked', reason: BLOCKED_REASON.consent });
      return;
    }
    // Closed BEFORE the write, as on the create form: a refused save paints its message on the form, and a
    // modal standing over it would hide the reason they are held.
    setGate(undefined);
    await update(true);
  }

  /** The one write. `consented` is true only when the candidate agreed to the modal on THIS visit. */
  async function update(consented: boolean): Promise<void> {
    /*
     * ⭐ **The screen's submit is a name ENTERED** [03-us-kad-6j-f AC-1a]: *First name* and the first letter
     * of the CHECK-IN surname factor, for the live socket's `session.started`. An empty *First name* gives
     * no candidate and sends nothing. It is the SUBMIT that enters it, write or no write.
     */
    enterSessionCandidate(sessionCandidateFromNames(values.firstName, factors.lastName));
    /*
     * ⚠️ **Only what the candidate changed travels** [0464 R3–R7] — see `changesFrom`. An untouched field is
     * absent, never `''` and never the record's own value; a cleared one is absent too, so city, state and
     * ZIP — which this screen does not draw — never travel at all.
     *
     * ⛔ **Nothing changed writes nothing** [0464 R3]: no request reaches Bullhorn, and the flow continues —
     * unless the candidate just gave the consent the record lacked, which is itself the thing to write
     * [0507]. With no matched id nothing is written either, which on this screen is a dev seed rather than a
     * real visit: an update needs the id, and a write without one is a CREATE — the duplicate KAP-7 forbids.
     */
    /*
     * A read that failed leaves the baseline empty, so the seeded on-file email would count as a change; the
     * lookup's `emailOnFile` is the record's address too [0030], and stands in for the one the read missed.
     */
    const changes = changesFrom(values, {
      ...baseline,
      email: baseline.email === '' ? (emailOnFile ?? '') : baseline.email,
    });
    if (candidateId === undefined || (Object.keys(changes).length === 0 && !consented)) {
      goTo(SCREEN.arrivalReason);
      return;
    }
    setSaving(true);
    const stillTheirs = captureCandidateSession();
    const outcome = await saveProfile({
      ...changes,
      ...(consented ? { consent: true } : {}),
      candidateId,
    });
    /*
     * ⛔ **A save that lands after Exit is dropped** [KID-1, KID-2: one visit, one person] — the same rule as
     * the profile page. This screen writes no id into the session, but its answer MOVES it: applied after
     * Exit and YES, it took Welcome, or the next person's first screen, to the arrival menu of a visit
     * nobody had started.
     */
    if (!stillTheirs()) return;
    /*
     * **A refused save HOLDS them here; an unreachable API does not.** The two used to be one value, and
     * collapsing them is what let a 400 look like success: the correction bounced, the screen moved on to
     * the arrival menu, and the candidate left believing ISS now had their new number [observed on dev,
     * 2026-09-07].
     *
     * The split is the distinction BR-K7 actually draws. A refusal names something the person standing
     * there can fix, so telling them and waiting is the opposite of a dead end. An unreachable API names
     * nothing they can act on — there is no member of staff to ask and no retry that helps — so they carry
     * on and the gap is the operator's to see, exactly as before.
     */
    if (outcome.kind === 'rejected') {
      const marked = rejectionErrors(outcome.fields);
      /*
       * A refusal that marks NOTHING leaves the candidate with only the summary line and no control to fix
       * — a field this screen does not draw, such as `city`. That is the operator's to see, so it is an alert.
       * ⛔ Field NAMES only: this prints on a shared device's console, and a value is somebody's data.
       */
      if (Object.keys(marked).length === 0) {
        console.error({
          msg: 'profile update refused on a field this screen does not draw',
          alert: 'profile_update_unmappable_refusal',
          fields: outcome.fields,
        });
      }
      setErrors(marked);
      setRefusals((n) => n + 1);
      setNotSaved(true);
      setSaving(false);
      return;
    }
    goTo(SCREEN.arrivalReason);
  }

  const err = (key: MarkableField): string | undefined =>
    errors[key] === true ? t(ERROR_MESSAGE[key]) : undefined;

  // Everything drawn AROUND the value travels as one argument rather than as positional ones: `$` and
  // `/hr` are a pair, the hint belongs to the same control, and four parameters is over this repo's limit.
  const text = (
    key: MarkableField,
    label: string,
    extra: {
      readonly prefix?: string;
      readonly suffix?: string;
      readonly hint?: string;
    } = {},
  ) => (
    <Field
      error={err(key)}
      hint={extra.hint}
      id={`profile-${key}`}
      label={label}
      onChange={(v) => {
        set(key, v);
      }}
      prefix={extra.prefix}
      suffix={extra.suffix}
      value={values[key]}
    />
  );

  /*
   * A decline REPLACES this screen without unmounting it, so every correction typed here survives the way
   * back — `Blocked · consent` promises *"Nothing you typed has been lost."* — and the way back is the
   * modal [0160; 0314]. Consent is the only refusal this screen has, and only when the record holds none.
   */
  if (gate?.stage === 'blocked') {
    return (
      <BlockedScreen
        onExit={endCandidateSession}
        onGoBack={() => {
          setGate({ stage: 'consent' });
        }}
        reason={BLOCKED_REASON.consent}
      />
    );
  }

  return (
    <KioskScreen>
      <KioskCard
        footer={
          /*
           * The action HUGS its label and sits right from `sm` up, and stretches at 375.
           *
           * Measured across the live frames on 2026-09-02: every one of these draws an auto-width primary
           * on the right — 122 on Application and Fit questions, 130 on See a rep, 224 on Returning profile,
           * 86 on Submitted — and 343 full-width at 375. The build shipped `fullWidth` everywhere, so at
           * desktop the button ran the whole 720px band. No jobs and the associate card keep `fullWidth`,
           * because their frames really do draw it full width; this is not a blanket rule.
           */
          <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-center sm:justify-end">
            <Button disabled={saving || loading} onClick={confirm}>
              {t('profileUpdate.continue')}
            </Button>
          </div>
        }
        header={
          <>
            {/*
              The Exit question, not `endCandidateSession` and never `goTo(SCREEN.welcome)` [ISS-149].
              Back out of this page leaves the visit, so it asks first — the same overlay the top bar's
              `Exit` opens — and NO keeps every edit made here. YES is `endCandidateSession`, and `goTo`
              would be wrong for the reason it always was: it carries no invariants by design, so it left
              the last-4 SSN, the birthday, the email and the matched id sitting in the store, on the ONE
              screen where the idle timer is deliberately disarmed because Welcome is meant to be the
              cleared state.
            */}
            <BackLink onClick={askToExit} />
            <StatusBadge label={t('badge.kiosk')} />
          </>
        }
      >
        <div className="flex w-full flex-col items-start gap-6 pb-8">
          <div className="flex w-full flex-col items-start gap-1">
            <h1
              className="kiosk-heading-focus w-full text-2xl font-bold leading-7 tracking-[-0.36px] text-black"
              ref={heading}
              tabIndex={-1}
            >
              {t('profileUpdate.heading')}
            </h1>
            <p className="w-full text-sm leading-6 text-[var(--muted-foreground)]">
              {t('profileUpdate.subtitle')}
            </p>
          </div>

          <Group title={t('application.personal')}>
            <Row>
              {text('firstName', t('application.firstName'))}
              {text('lastName', t('application.lastName'))}
            </Row>
            <Row>
              {text('email', t('application.email'))}
              {/* «Phone» here, «Phone number» on the create form — the two frames disagree and this screen
                  was borrowing the other one's word. See the note at `profileUpdate.phone`. */}
              {text('phone', t('profileUpdate.phone'))}
            </Row>
          </Group>

          <Group title={t('profileUpdate.preferences')}>
            <Row>
              {/*
                A SELECT, not a text box — the frame draws it with a chevron and the placeholder «Any
                shift». It was a free-text input, which asked a returning associate to guess ISS's own
                vocabulary and would have written whatever they typed into a picklist field.

                The options are Bullhorn's, read from `JobOrder.customText11` («Shift Type») on 2026-09-02:
                First / Second / Third Shift, Weekend, Various Shifts. Those are the values the openings
                themselves carry, so a preference expressed here uses the same words as the jobs it is
                meant to match.
              */}
              {/* `w-full` around it, the way the create screen wraps its own Select: on its own the

                  component hugged its content at 91px, which wrapped the label onto a second line and made

                  the block 84 tall against a drawn 64. */}

              <div className="w-full">
                <Select
                  id="profile-shiftPreference"
                  label={t('profileUpdate.shiftPreference')}
                  onChange={(v) => {
                    set('shiftPreference', v);
                  }}
                  options={SHIFT_OPTIONS}
                  placeholder={t('profileUpdate.anyShift')}
                  value={values.shiftPreference}
                />
              </div>
              {/* The `$` AND the `/hr` are the same adornments the create screen carries — the two screens
                  ask for the same Bullhorn field and must not disagree about its unit [Operator,
                  2026-09-07]. This screen carried only the `$`, which is the disagreement that comment was
                  written to forbid.

                  ⭐ **The hint is drawn here too, and was not being rendered.** `04 · Returning profile`
                  draws «Enter your desired hourly rate.» as a `Helper text` node under this control at all
                  three widths (Desktop `12021:41053`, iPad `12021:41016`, Mobile `12021:40980`, read live
                  2026-09-15) — the same sentence, from the same `Helper text` node, that the create form
                  already shows. The string was in the dictionary and correct; only this call site dropped
                  it, which is why a sweep that compared dictionary VALUES against the frames could not see
                  it [Operator, 2026-09-14: the drawn text is the text we show]. */}
              {text('payRate', t('application.payRate'), {
                hint: t('application.payRate.hint'),
                prefix: '$',
                suffix: '/hr',
              })}
            </Row>
            {/*
              `MM/DD/YYYY` on screen, ISO in the model — the same control the create screen uses, for the
              same reason: `dateAvailable` takes `YYYY-MM-DD` and Bullhorn silently drops anything else, so
              the stored shape is fixed while the displayed one is a product choice [Operator, 2026-09-07].

              This was free text, then a native `type="date"`. Neither could be made to show a chosen order:
              free text let a person type anything, and the native control formats itself from the browser's
              locale with no way to override it.
            */}
            {/*
              `min` is TODAY [decision 0180]. This is the screen the asymmetry exists FOR: the record's own
              old availability date is read back into this field, stays shown, stays selected in the picker
              and saves untouched — `isSavableStartDate` and the api both take it. The floor only stops the
              candidate ENTERING another past day, picked or typed [RK-037, Operator, 2026-10-19]: it is
              applied by `DateField` to what is entered, never to what the record brought. Tightening either
              side dead-ends the returning flow.
            */}
            <DateField
              error={err('dateAvailable')}
              id="profile-dateAvailable"
              label={t('profileUpdate.dateAvailable')}
              min={todayIso()}
              onChange={(v) => {
                set('dateAvailable', v);
              }}
              value={values.dateAvailable}
            />
          </Group>

          <Group title={t('profileUpdate.contact')}>
            {text('homeAddress', t('profileUpdate.homeAddress'))}
            <Row>
              {text('emergencyContact', t('profileUpdate.emergencyContact'))}
              <Field
                error={err('emergencyPhone')}
                id="profile-emergencyPhone"
                inputMode="numeric"
                label={t('profileUpdate.emergencyPhone')}
                maxLength={10}
                onChange={(v) => {
                  // Same rule as the candidate's own number: Bullhorn wants ten digits and enforces none of
                  // it, so a number typed with punctuation is cleaned here rather than stored unusable.
                  set('emergencyPhone', digitsOnly(v));
                }}
                value={values.emergencyPhone}
              />
            </Row>
          </Group>

          <Callout tone="warning">{t('profileUpdate.sensitive')}</Callout>

          {/*
            The one thing on this screen the Figma file does not draw: **no frame draws a save that failed.**
            So it is assembled from the design system's own error treatment rather than invented — the same
            `--error` text a `Field` shows, at screen level — and it renders only after a Continue that got
            nowhere, so the drawn state of the screen is byte-for-byte what the frames measure
            [convention skill § 8: where no frame exists, say so at the call site and raise it].

            It sits last, immediately above the footer's Continue, because that is where the candidate is
            looking when nothing happens. The FIELD messages say which control; this says that the screen is
            waiting rather than broken — and it is the only thing shown when the API refuses a field this
            screen does not draw.
          */}
          {notSaved && (
            <p className="text-sm leading-5 text-[var(--error)]" role="alert">
              {t('profileUpdate.saveRejected')}
            </p>
          )}
        </div>
      </KioskCard>

      {gate?.stage === 'consent' && (
        <ConsentDialog
          onAnswer={(consented) => {
            // `answerConsent` owns its promise and never rejects: every failure becomes a value inside it.
            void answerConsent(consented);
          }}
        />
      )}
    </KioskScreen>
  );
}

function Group({
  title,
  children,
}: {
  readonly title: string;
  readonly children: React.ReactNode;
}) {
  return (
    <section className="flex w-full flex-col gap-4">
      {/* 12/16 bold, 1.44px tracking — the frame's value; see the note on `ApplicationScreen`'s copy. */}
      <h2 className="text-xs font-bold uppercase leading-4 tracking-[1.44px] text-[var(--muted-foreground)]">
        {title}
      </h2>
      {children}
    </section>
  );
}

function Row({ children }: { readonly children: React.ReactNode }) {
  return <div className="flex w-full flex-col gap-4 sm:flex-row">{children}</div>;
}
